import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { PaginatedTable } from 'components/PaginatedTable';
import { ErrorMessage } from 'components/ErrorMessage';
import {
  Checkbox,
  DefaultButton,
  Dialog,
  DialogContent,
  DialogTitle,
  makeStyles,
  SortableTableHeaderCell,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@bb-ui/react-library';
import {
  OnSortChangedParams,
  SortDirection,
} from '@bb-ui/react-library/dist/components/SortableTable/SortableTable.types';
import { TableSkeleton } from 'pages/Cohorts/shared';
import { Lock, StatusAway, Unlock, Warning } from '@bb-ui/icons/dist/small';
import { ACTIVE_STATUSES, PENDING_STATUSES, Status } from '@lct-jobs/core';
import { TextField } from '@bb-ui/react-library/dist/components/TextField';
import { MenuItem } from '@bb-ui/react-library/dist/components/MenuItem';
import { Refresh } from '@bb-ui/icons/dist/medium';
import { LinearProgress } from '@bb-ui/react-library/dist/components/Progress';
import { JobStatusIcon } from 'components/JobStatusIcon/JobStatusIcon';
import { FAILED_JOB_STATUSES } from 'components/JobExecution/failedJobStatuses';
import { ReleaseStageUpgradeButton } from 'pages/ReleaseStages/ReleaseStageUpgradeButton';
import { Link } from 'react-router-dom';
import { AddReleaseStageSitesDialog } from 'dialogs/AddReleaseStageSitesDialog';
import { RemoveReleaseStageSitesDialog } from 'dialogs/RemoveReleaseStageSitesDialog';
import { ReleaseStageMember } from './ReleaseStageMembers.types';
import { SITE_TYPE_FILTERS, SiteTypeFilter, matchesSiteTypeFilter } from 'utils/siteType';
import { ReleaseStageUnlockActions } from './ReleaseStageUnlockActions';
import { ReleaseStageTargetVersionButton } from './ReleaseStageTargetVersionButton';
import { useReleaseStageMembers } from './useReleaseStageMembers';

const EMPTY_VALUE = '—';

const MEMBERS_PER_PAGE = { value: 25, options: [25, 50, 100] };

const DECOMMISSION_STATUSES = [
  'Decommissioned',
  'DecommissionedQueued',
  'DecommissionInProgress',
  'DecommissionFailed',
  'WaitDecommission',
];

const useStyles = makeStyles((theme) => ({
  actions: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  versionSummary: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
  },
  versionValue: {
    fontWeight: theme.typography.fontWeightBold,
  },
  emptyCell: {
    color: theme.palette.text.secondary,
  },
  jobStatus: {
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    whiteSpace: 'nowrap',
  },
  jobFilter: {
    minWidth: 160,
  },
  refreshButton: {
    marginLeft: 'auto',
  },
  /**
   * Fixed height so the bar coming and going never nudges the table, and the button
   * keeps its size because the loading does not live inside it.
   */
  /**
   * The line keeps its place whether or not anything is hidden, so the dialog does
   * not grow and shrink as the numbers change.
   */
  hiddenMembers: {
    minHeight: '1.5rem',
  },
  refreshBar: {
    height: 4,
    marginBottom: theme.spacing(1),
  },
  jobFilterLabel: {
    padding: 1,
  },
  /**
   * The running icon spins in the table, where it says a job is still going. An option
   * in a list is a label, not a job, so here the same symbol is held still.
   */
  stillIcon: {
    display: 'inline-flex',
    '& svg, & circle': {
      animation: 'none',
    },
  },
  jobFilterOption: {
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  jobStatusLink: {
    color: 'inherit',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  lockedIcon: {
    color: theme.palette.error.main,
  },
  /** Waiting is not a problem, so it reads as quiet as an unlocked site. */
  waitingIcon: {
    color: theme.palette.text.secondary,
  },
  /**
   * Not started is the one state that asks for a hand: the run is over and the site
   * was left behind. It is told apart from failed by colour and shape, not by colour
   * alone.
   */
  notStartedIcon: {
    color: theme.palette.warning.main,
  },
  /**
   * A site whose upgrade ended in error is read across the whole row, not by hunting
   * for the icon at the end of it.
   */
  failedRow: {
    '& td': {
      color: theme.palette.error.main,
    },
  },
  unlockedIcon: {
    color: theme.palette.text.secondary,
  },
  /**
   * The dialog takes the height it is allowed and keeps it, instead of measuring itself
   * against whatever the current page holds.
   */
  dialogPaper: {
    height: '100%',
    overflow: 'hidden',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  /**
   * The rows take whatever height is left and scroll inside it, so a page of wrapped
   * urls, a last page holding two rows and a filter that matches nothing all leave the
   * dialog exactly as tall as it was. The search and the pagination sit outside that
   * box and stay where the reader left them.
   */
  tableArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    '& [data-testid="paginated-table-rows"]': {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
    },
    /** Room the toolbar does not need, given back to the rows. */
    '& [data-testid="paginated-table-toolbar"]': {
      marginBottom: theme.spacing(2),
    },
    '& #paginated-table-sortable-table thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 2,
      backgroundColor: theme.palette.background.paper,
    },
    '& #paginated-table-sortable-table': {
      tableLayout: 'fixed',
    },
    '& #paginated-table-sortable-table th:nth-child(1)': { width: '3rem' },
    '& #paginated-table-sortable-table th:nth-child(2)': { width: '10%' },
    '& #paginated-table-sortable-table th:nth-child(3)': { width: '20%' },
    '& #paginated-table-sortable-table th:nth-child(4)': { width: '16%' },
    '& #paginated-table-sortable-table th:nth-child(5)': { width: '11%' },
    '& #paginated-table-sortable-table th:nth-child(6)': { width: '13%' },
    '& #paginated-table-sortable-table th:nth-child(7)': { width: '13%' },
    '& #paginated-table-sortable-table th:nth-child(8)': { width: '9%' },
    '& #paginated-table-sortable-table th:nth-child(9)': { width: '8%' },
    '& #paginated-table-sortable-table td:nth-child(3), & #paginated-table-sortable-table td:nth-child(6)':
      {
        overflowWrap: 'anywhere',
      },
  },
}));

type MemberColumn = {
  columnId: string;
  labelKey: string;
  value: (member: ReleaseStageMember) => string;
  align?: 'center';
  /**
   * Puts the explanation of the column in the header tooltip, in place of the sort
   * hint: the header holds one tooltip, and two of them opening at once read as noise.
   */
  hasHelp?: boolean;
};

const LOCK_COLUMN_ID = 'lockState';
const INSTALLATION_ID_COLUMN_ID = 'installationId';
const URL_COLUMN_ID = 'url';
const JOB_STATUS_COLUMN_ID = 'jobStatus';

/**
 * The status the release stage job service writes on every site a run reaches, before
 * the site has a job of its own to report. It is not a status of `lct-jobs`: it says
 * this run has not got to that site yet.
 */
const WAITING_STATUS = 'WAITING';

/**
 * Also ours: the run that reached the site has finished handing out jobs and never
 * started one for it. Waiting says the turn of the site has not come; this says it has
 * come and gone, so the site will not move until someone runs it again.
 */
const NOT_STARTED_STATUS = 'NOT_STARTED';

/**
 * The groups come from `@lct-jobs/core`, the same ones the other job screens in
 * Houston read, so a status added upstream lands somewhere without us noticing.
 * Only the split between a job that finished well and one that did not is ours.
 */
/**
 * No `pending` option: `lct-jobs` publishes an event when the execution is queued and
 * again when it ends, never while it runs, so a site sits in QUEUED for the whole
 * job. Queued is therefore read as running, and a pending filter would always be
 * empty. Waiting is ours and comes first, because it is where every run starts.
 * PENDING_SCHEDULED is the exception: an upgrade booked for later is not in flight,
 * and an operator who books one needs to be able to find it again.
 */
const JOB_STATUS_FILTERS = [
  'all',
  'waiting',
  'notStarted',
  'scheduled',
  'running',
  'completed',
  'failed',
] as const;

type JobStatusFilter = (typeof JOB_STATUS_FILTERS)[number];

/**
 * One status per option, so the dropdown can show the same symbol the column shows.
 * `all` has none: it is not a state, it is the absence of a filter.
 */
const JOB_FILTER_STATUSES: Partial<Record<JobStatusFilter, string>> = {
  waiting: WAITING_STATUS,
  notStarted: NOT_STARTED_STATUS,
  scheduled: Status.PENDING_SCHEDULED,
  running: Status.IN_PROGRESS,
  completed: Status.COMPLETED,
  failed: Status.FAILED,
};

const isOneOf = (group: readonly string[], status?: string): boolean =>
  status !== undefined && group.includes(status);

const hasFailed = (status?: string): boolean => FAILED_JOB_STATUSES.has(status as Status);

const isWaiting = (status?: string): boolean => status === WAITING_STATUS;

const isNotStarted = (status?: string): boolean => status === NOT_STARTED_STATUS;

const isScheduled = (status?: string): boolean => status === Status.PENDING_SCHEDULED;

/**
 * The cell shows the icon alone, like the lock one. This is what the tooltip and the
 * screen reader say, written the way the job screens write it.
 */
const readableStatus = (status: string): string => status.toLowerCase().replace(/_/g, ' ');

/**
 * A queued job is drawn as a running one, and gets the icon the job screens give to
 * IN_PROGRESS: `lct-jobs` speaks when the execution is queued and again when it ends,
 * so queued is what a job in flight looks like from here.
 */
const isJobRunning = (status?: string): boolean =>
  isOneOf(ACTIVE_STATUSES, status) || (isOneOf(PENDING_STATUSES, status) && !isScheduled(status));

const iconStatus = (status?: string): Status =>
  (isJobRunning(status) ? Status.IN_PROGRESS : status) as Status;

const matchesJobFilter = (member: ReleaseStageMember, filter: JobStatusFilter): boolean => {
  switch (filter) {
    case 'waiting':
      return isWaiting(member.JobStatus);
    case 'notStarted':
      return isNotStarted(member.JobStatus);
    case 'scheduled':
      return isScheduled(member.JobStatus);
    case 'running':
      return isJobRunning(member.JobStatus);
    case 'completed':
      return member.JobStatus === Status.COMPLETED;
    case 'failed':
      return hasFailed(member.JobStatus);
    default:
      return true;
  }
};

const LOCK_STATUS_FILTERS = ['all', 'locked', 'unlocked'] as const;

type LockStatusFilter = (typeof LOCK_STATUS_FILTERS)[number];

/** A missing projection is not evidence that the authoritative state is unlocked. */
const matchesLockFilter = (member: ReleaseStageMember, filter: LockStatusFilter): boolean => {
  switch (filter) {
    case 'locked':
      return member.Locked === true;
    case 'unlocked':
      return member.Locked === false;
    default:
      return true;
  }
};

const lockStateValue = (member: ReleaseStageMember): string => {
  if (member.Locked === true) {
    return 'Locked';
  }
  if (member.Locked === false) {
    return 'Unlocked';
  }
  return '';
};

/**
 * Built once: localeCompare rebuilds its collation on every call, which a stage of
 * a couple of thousand sites pays on every sort.
 */
const collator = new Intl.Collator();

/**
 * What the search box matches a member against, computed once per member instead
 * of once per keystroke.
 */
const searchKeyOf = (member: ReleaseStageMember): string =>
  `${member.Url ?? member.TenantName} ${member.ClientName ?? ''} ${
    member.InstallationId ?? ''
  } ${member.TenantId}`.toLowerCase();

/**
 * A member without a snapshot has nothing to show but dashes, and one on its way
 * out is not worth a row either. InstanceUpdated is the field the release stage
 * job writes for every snapshot, so it tells a missing snapshot from a missing field.
 */
const isRunning = (member: ReleaseStageMember): boolean => {
  if (member.InstanceUpdated === undefined) {
    return false;
  }
  return !member.InstanceStatus || !DECOMMISSION_STATUSES.includes(member.InstanceStatus);
};

/**
 * Installation IDs stay inside Houston. Site URLs open the actual Learn site.
 */
const memberCellContent = (
  columnId: string,
  cellValue: string,
  member: ReleaseStageMember,
): React.ReactNode => {
  if (columnId === INSTALLATION_ID_COLUMN_ID && cellValue) {
    return (
      <Link
        to={`/learn-site/${member.TenantId}/information`}
        data-testid={`release-stage-members-site-info-${member.TenantId}`}
      >
        {cellValue}
      </Link>
    );
  }

  if (columnId === URL_COLUMN_ID && cellValue) {
    const href = /^https?:\/\//i.test(cellValue) ? cellValue : `https://${cellValue}`;
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        data-testid={`release-stage-members-site-url-${member.TenantId}`}
      >
        {cellValue}
      </a>
    );
  }
  return cellValue || EMPTY_VALUE;
};

const MEMBER_COLUMNS: MemberColumn[] = [
  {
    columnId: 'installationId',
    labelKey: 'installationId',
    value: (member) => member.InstallationId ?? '',
  },
  { columnId: 'url', labelKey: 'url', value: (member) => member.Url || member.TenantName },
  { columnId: 'client', labelKey: 'client', value: (member) => member.ClientName ?? '' },
  { columnId: 'fleet', labelKey: 'fleet', value: (member) => member.FleetId ?? '' },
  {
    columnId: 'siteLearnVersion',
    labelKey: 'siteLearnVersion',
    value: (member) => member.LearnVersion ?? '',
  },
  { columnId: 'status', labelKey: 'status', value: (member) => member.InstanceStatus ?? '' },
  {
    columnId: LOCK_COLUMN_ID,
    labelKey: 'lockState',
    value: lockStateValue,
    align: 'center',
  },
  {
    columnId: JOB_STATUS_COLUMN_ID,
    labelKey: 'jobStatus',
    value: (member) => member.JobStatus ?? '',
    align: 'center',
    hasHelp: true,
  },
];

/**
 * The status carries a link to its execution, which is where the operator goes when
 * a site fails. The detail page is the tenant one: the global `/jobs/executions/:id`
 * route answers 404 for these executions. Without an execution id it stays text.
 */
/**
 * `JobStatusIcon` draws nothing for a status `lct-jobs` does not publish, and teaching
 * it ours would reach every other job screen. Waiting is drawn here instead, in the
 * quiet grey the lock column uses for a site that is not asking for attention.
 */
const jobStatusIconFor = (status?: string): React.ReactNode => {
  if (isWaiting(status)) {
    return <StatusAway />;
  }
  if (isNotStarted(status)) {
    return <Warning />;
  }
  return <JobStatusIcon status={iconStatus(status)} />;
};

/** The colour our own statuses take; the `lct-jobs` ones colour themselves. */
const jobStatusIconClass = (classes: ReturnType<typeof useStyles>, status?: string): string => {
  if (isWaiting(status)) {
    return classes.waitingIcon;
  }
  if (isNotStarted(status)) {
    return classes.notStartedIcon;
  }
  return '';
};

const JobStatusCell: React.FunctionComponent<{ member: ReleaseStageMember }> = ({ member }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const notStarted = isNotStarted(member.JobStatus);
  const status = notStarted
    ? t('releaseStageMembers.notStartedHelp')
    : readableStatus(member.JobStatus as string);
  const content = (
    <span
      className={`${classes.jobStatus} ${jobStatusIconClass(classes, member.JobStatus)}`}
      role="img"
      aria-label={status}
      title={status}
    >
      {jobStatusIconFor(member.JobStatus)}
    </span>
  );

  /**
   * A site the run never started has no execution of its own to open. The run does,
   * and its parent-to-child failures say why the site was left out.
   */
  if (notStarted && member.JobRunId) {
    return (
      <Link
        to={`/tenants/${member.ReleaseStageId}/executions/${member.JobRunId}`}
        target="_blank"
        rel="noreferrer"
        className={classes.jobStatusLink}
        data-testid={`release-stage-members-job-${member.TenantId}`}
      >
        {content}
      </Link>
    );
  }

  if (!member.JobExecutionId) {
    return content;
  }

  return (
    <Link
      to={`/learn-site/${member.TenantId}/executions/${member.JobExecutionId}`}
      target="_blank"
      rel="noreferrer"
      className={classes.jobStatusLink}
      data-testid={`release-stage-members-job-${member.TenantId}`}
    >
      {content}
    </Link>
  );
};

export interface ReleaseStageMembersDialogProps {
  id: string;
  open: boolean;
  onClose: () => void;
  releaseStageId: string;
  releaseStageName: string;
  targetReleaseVersion: string;
  lastReleaseVersion?: string;
  onReleaseStageUpdated: () => void;
}

export const ReleaseStageMembersDialog: React.FunctionComponent<ReleaseStageMembersDialogProps> = ({
  id,
  open,
  onClose,
  releaseStageId,
  releaseStageName,
  targetReleaseVersion,
  lastReleaseVersion,
  onReleaseStageUpdated,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { members, loading, failed, refreshing, refresh, updateLockStatuses } =
    useReleaseStageMembers(releaseStageId, open);

  const reload = React.useCallback(() => {
    onReleaseStageUpdated();
    refresh();
  }, [onReleaseStageUpdated, refresh]);

  const [jobFilter, setJobFilter] = React.useState<JobStatusFilter>('all');
  const [lockFilter, setLockFilter] = React.useState<LockStatusFilter>('all');
  const [siteTypeFilter, setSiteTypeFilter] = React.useState<SiteTypeFilter>('all');

  const running = React.useMemo(() => members.filter(isRunning), [members]);

  const rows = React.useMemo(
    () =>
      running
        .filter((member) => matchesJobFilter(member, jobFilter))
        .filter((member) => matchesLockFilter(member, lockFilter))
        .filter((member) => matchesSiteTypeFilter(member, siteTypeFilter)),
    [running, jobFilter, lockFilter, siteTypeFilter],
  );

  const hiddenCount = members.length - running.length;

  /** How many sites sit in each state, so the filter options can show their counts. */
  const jobCounts = React.useMemo(
    () =>
      JOB_STATUS_FILTERS.reduce(
        (counts, option) => ({
          ...counts,
          [option]: running.filter((member) => matchesJobFilter(member, option)).length,
        }),
        {} as Record<JobStatusFilter, number>,
      ),
    [running],
  );

  /**
   * The icon belongs in the open list, where it tells the options apart. On the
   * closed control it only crowds the box, so what stays is the name and the count.
   */
  const jobFilterLabel = (option: JobStatusFilter): string => {
    const name = t(`releaseStageMembers.jobStatusFilters.${option}`);
    return option === 'all' ? name : `${name} (${jobCounts[option]})`;
  };

  /**
   * How the stage stands at a glance, without opening the filter: every state some
   * site is in, with the same names and counts the filter gives them.
   */
  const jobSummary = JOB_STATUS_FILTERS.filter(
    (option) => option !== 'all' && jobCounts[option] > 0,
  )
    .map(jobFilterLabel)
    .join(' · ');

  const lockCounts = React.useMemo(
    () =>
      LOCK_STATUS_FILTERS.reduce(
        (counts, option) => ({
          ...counts,
          [option]: running.filter((member) => matchesLockFilter(member, option)).length,
        }),
        {} as Record<LockStatusFilter, number>,
      ),
    [running],
  );

  const lockFilterLabel = (option: LockStatusFilter): string => {
    const name = t(`releaseStageMembers.lockStateFilters.${option}`);
    return option === 'all' ? name : `${name} (${lockCounts[option]})`;
  };

  const siteTypeCounts = React.useMemo(
    () =>
      SITE_TYPE_FILTERS.reduce(
        (counts, option) => ({
          ...counts,
          [option]: running.filter((member) => matchesSiteTypeFilter(member, option)).length,
        }),
        {} as Record<SiteTypeFilter, number>,
      ),
    [running],
  );

  const siteTypeFilterLabel = (option: SiteTypeFilter): string => {
    const name = t(`releaseStageMembers.siteTypeFilters.${option}`);
    return option === 'all' ? name : `${name} (${siteTypeCounts[option]})`;
  };

  const [selectedTenantIds, setSelectedTenantIds] = React.useState<string[]>([]);
  const [addSitesOpen, setAddSitesOpen] = React.useState(false);
  const [removeSitesOpen, setRemoveSitesOpen] = React.useState(false);
  const [removeSiteTargets, setRemoveSiteTargets] = React.useState<ReleaseStageMember[]>([]);
  const [membershipChanged, setMembershipChanged] = React.useState(false);
  const [sortParams, setSortParams] = React.useState<Partial<OnSortChangedParams>>({
    sortColumnId: 'url',
    sortDirection: 'asc',
  });

  React.useEffect(() => {
    setSelectedTenantIds([]);
    setAddSitesOpen(false);
    setRemoveSitesOpen(false);
    setRemoveSiteTargets([]);
    setMembershipChanged(false);
  }, [open, releaseStageId]);

  React.useEffect(() => {
    /**
     * While a reload is in flight the member list empties for a moment; pruning
     * then would drop a selection nothing has taken away yet. Wait until the
     * fresh list has landed, so removed tenants leave the selection and the
     * ones that failed to go stay picked for another try.
     */
    if (refreshing || failed) return;
    const visibleTenantIds = new Set(running.map((member) => member.TenantId));
    setSelectedTenantIds((current) => {
      const next = current.filter((tenantId) => visibleTenantIds.has(tenantId));
      return next.length === current.length ? current : next;
    });
  }, [failed, running, refreshing]);

  const toggleMember = (tenantId: string) => {
    setSelectedTenantIds((previous) =>
      previous.includes(tenantId)
        ? previous.filter((selectedId) => selectedId !== tenantId)
        : [...previous, tenantId],
    );
  };

  const rowTenantIds = React.useMemo(() => new Set(rows.map((member) => member.TenantId)), [rows]);

  const selectedRowTenantIds = React.useMemo(
    () => selectedTenantIds.filter((tenantId) => rowTenantIds.has(tenantId)),
    [rowTenantIds, selectedTenantIds],
  );
  const selectedRowCount = selectedRowTenantIds.length;

  const toggleAllMembers = () => {
    setSelectedTenantIds((previous) => {
      const next = new Set(previous);
      if (rows.length > 0 && rows.every((member) => next.has(member.TenantId))) {
        return previous.filter((tenantId) => !rowTenantIds.has(tenantId));
      }

      for (const member of rows) {
        next.add(member.TenantId);
      }
      return Array.from(next);
    });
  };

  /**
   * The members the removal would act on: everything selected that is still shown,
   * the same reading of the selection the unlock action uses.
   */
  const selectedRowTenantIdSet = React.useMemo(
    () => new Set(selectedRowTenantIds),
    [selectedRowTenantIds],
  );
  const selectedMembers = React.useMemo(
    () => running.filter((member) => selectedRowTenantIdSet.has(member.TenantId)),
    [running, selectedRowTenantIdSet],
  );
  const selectedInstallationIds = React.useMemo(
    () => selectedMembers.flatMap((member) => member.InstallationId ?? []),
    [selectedMembers],
  );
  const selectedUpgradeHasMissingInstallationId =
    selectedMembers.length !== selectedInstallationIds.length;

  /**
   * A mutation went through: read the membership again without reloading the
   * outer stages table. Reloading that table while an add has per-site errors
   * would unmount both dialogs and hide the errors before the reader could act.
   * Selection needs no handling here: when the fresh members arrive, tenants
   * that are gone drop out and failed ones stay picked for another try.
   */
  const handleMembershipChanged = () => {
    refresh();
    setMembershipChanged(true);
  };

  const openRemoveSites = () => {
    setRemoveSiteTargets(selectedMembers);
    setRemoveSitesOpen(true);
  };

  const closeRemoveSites = () => {
    setRemoveSitesOpen(false);
    setRemoveSiteTargets([]);
  };

  const handleSitesRemoved = (removedTenantIds: string[]) => {
    const removed = new Set(removedTenantIds);
    setRemoveSiteTargets((current) => current.filter((member) => !removed.has(member.TenantId)));
    handleMembershipChanged();
  };

  /**
   * The stage count can catch up once this view is leaving, when a parent reload
   * cannot interrupt an in-progress add/remove result.
   */
  const handleClose = () => {
    if (membershipChanged) {
      onReleaseStageUpdated();
    }
    onClose();
  };

  const searchKeys = React.useMemo(
    () => new Map(rows.map((member) => [member.TenantId, searchKeyOf(member)])),
    [rows],
  );

  const search = (filter: string) => {
    const wanted = filter.toLowerCase();
    return (member: ReleaseStageMember) =>
      (searchKeys.get(member.TenantId) ?? searchKeyOf(member)).includes(wanted);
  };

  const sortedRows = React.useMemo(() => {
    const { sortColumnId, sortDirection } = sortParams;
    const orderSign = sortDirection === 'asc' ? 1 : -1;
    const column = MEMBER_COLUMNS.find(({ columnId }) => columnId === sortColumnId);

    if (!column) {
      return rows;
    }

    const keyed = rows.map((member) => ({ member, key: column.value(member) }));
    keyed.sort((firstRow, secondRow) => orderSign * collator.compare(firstRow.key, secondRow.key));

    return keyed.map(({ member }) => member);
  }, [rows, sortParams]);

  const getAriaSortMessage = (columnId?: string, sortDirection?: SortDirection) => {
    const columnLabel = t(`releaseStageMembers.columns.${columnId}`);
    const orderLabel =
      sortDirection === 'asc'
        ? t('global.paginatedTable.ascending')
        : t('global.paginatedTable.descending');
    return t('global.paginatedTable.sortedAriaMessage', { columnLabel, orderLabel });
  };

  const cellFor = (
    columnId: string,
    cellValue: string,
    member: ReleaseStageMember,
  ): React.ReactNode => {
    if (columnId === JOB_STATUS_COLUMN_ID && member.JobStatus) {
      return <JobStatusCell member={member} />;
    }

    if (columnId === LOCK_COLUMN_ID) {
      if (member.Locked === undefined) {
        return (
          <span
            className={classes.jobStatus}
            aria-label={t('releaseStageMembers.lockUnavailable', {
              url: member.Url ?? member.TenantName,
            })}
            data-lock-state="unknown"
            data-testid={`release-stage-members-lock-${member.TenantId}`}
          >
            {EMPTY_VALUE}
          </span>
        );
      }

      return (
        <span
          role="img"
          className={`${classes.jobStatus} ${
            member.Locked ? classes.lockedIcon : classes.unlockedIcon
          }`}
          aria-label={t(
            member.Locked ? 'releaseStageMembers.locked' : 'releaseStageMembers.unlocked',
            { url: member.Url ?? member.TenantName },
          )}
          data-lock-state={member.Locked ? 'locked' : 'unlocked'}
          data-testid={`release-stage-members-lock-${member.TenantId}`}
        >
          {member.Locked ? <Lock /> : <Unlock />}
          {t(
            member.Locked
              ? 'releaseStageMembers.lockStates.locked'
              : 'releaseStageMembers.lockStates.unlocked',
          )}
        </span>
      );
    }

    return memberCellContent(columnId, cellValue, member);
  };

  let content: React.ReactNode;

  if (loading) {
    content = (
      <Table data-testid="fnds-release-stage-members-init">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            {MEMBER_COLUMNS.map(({ columnId, labelKey }) => (
              <TableCell key={columnId}>{t(`releaseStageMembers.columns.${labelKey}`)}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableSkeleton
            rows={MEMBERS_PER_PAGE.value}
            columns={MEMBER_COLUMNS.length}
            columnWidths={['medium', 'wide', 'wide', 'medium', 'medium', 'medium', 'medium']}
            showCheckbox={true}
          />
        </TableBody>
      </Table>
    );
  } else if (failed) {
    content = (
      <ErrorMessage
        title={t('releaseStageMembers.loadError')}
        message={t('releaseStageMembers.loadError')}
        variant="block"
        data-testid="fnds-release-stage-members-no-data"
      />
    );
  } else if (running.length === 0) {
    /**
     * Only when the stage itself has nothing to show. A filter that matches nothing
     * keeps the table, because taking it away takes the filter with it and there is
     * no way back.
     */
    content = (
      <Typography data-testid="fnds-release-stage-members-empty">
        {t('releaseStageMembers.noData')}
      </Typography>
    );
  } else {
    content = (
      <PaginatedTable
        onSortChanged={(nextSortParams) => {
          setSortParams(nextSortParams);
          return true;
        }}
        getSortChangedAriaMessage={getAriaSortMessage}
        toolbarEnd={
          <>
            <TextField
              select
              id="release-stage-members-site-type-filter"
              label={t('releaseStageMembers.filterBySiteType')}
              value={siteTypeFilter}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setSiteTypeFilter(event.target.value as SiteTypeFilter)
              }
              className={classes.jobFilter}
              InputLabelProps={{ className: classes.jobFilterLabel }}
              SelectProps={{
                renderValue: (value: unknown) => siteTypeFilterLabel(value as SiteTypeFilter),
              }}
              floatingLabel
            >
              {SITE_TYPE_FILTERS.map((option) => (
                <MenuItem key={option} value={option}>
                  {siteTypeFilterLabel(option)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              id="release-stage-members-lock-filter"
              label={t('releaseStageMembers.filterByLockState')}
              value={lockFilter}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setLockFilter(event.target.value as LockStatusFilter)
              }
              className={classes.jobFilter}
              InputLabelProps={{ className: classes.jobFilterLabel }}
              SelectProps={{
                renderValue: (value: unknown) => lockFilterLabel(value as LockStatusFilter),
              }}
              floatingLabel
            >
              {LOCK_STATUS_FILTERS.map((option) => (
                <MenuItem key={option} value={option}>
                  <span className={classes.jobFilterOption}>
                    {option !== 'all' && (
                      <span className={classes.stillIcon}>
                        {option === 'locked' ? <Lock /> : <Unlock />}
                      </span>
                    )}
                    {lockFilterLabel(option)}
                  </span>
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              id="release-stage-members-job-filter"
              label={t('releaseStageMembers.filterByJobStatus')}
              value={jobFilter}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setJobFilter(event.target.value as JobStatusFilter)
              }
              className={classes.jobFilter}
              InputLabelProps={{ className: classes.jobFilterLabel }}
              SelectProps={{
                renderValue: (value: unknown) => jobFilterLabel(value as JobStatusFilter),
              }}
              floatingLabel
            >
              {JOB_STATUS_FILTERS.map((option) => (
                <MenuItem key={option} value={option}>
                  <span className={classes.jobFilterOption}>
                    {JOB_FILTER_STATUSES[option] && (
                      <span
                        className={`${classes.stillIcon} ${jobStatusIconClass(
                          classes,
                          JOB_FILTER_STATUSES[option],
                        )}`}
                      >
                        {jobStatusIconFor(JOB_FILTER_STATUSES[option])}
                      </span>
                    )}
                    {jobFilterLabel(option)}
                  </span>
                </MenuItem>
              ))}
            </TextField>
          </>
        }
        searchBoxProps={{ label: t('releaseStageMembers.searchLabel') }}
        perPageProps={MEMBERS_PER_PAGE}
        sortedData={sortedRows}
        search={search}
        noMatchesMessage={(searchExpression) =>
          t('releaseStageMembers.noMatch', { searchExpression })
        }
        renderHead={() => (
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={rows.length > 0 && selectedRowCount === rows.length}
                  indeterminate={selectedRowCount > 0 && selectedRowCount < rows.length}
                  disabled={rows.length === 0}
                  onChange={toggleAllMembers}
                  inputProps={{ 'aria-label': t('releaseStageMembers.selectAll') }}
                  data-testid="release-stage-members-select-all"
                />
              </TableCell>
              {MEMBER_COLUMNS.map(({ columnId, labelKey, align, hasHelp }) => (
                <SortableTableHeaderCell
                  key={columnId}
                  id={`release-stage-members-header-${columnId}`}
                  columnId={columnId}
                  tooltip={t(
                    hasHelp
                      ? `releaseStageMembers.columnHelp.${labelKey}`
                      : `releaseStageMembers.sortBy.${labelKey}`,
                  )}
                  tableCellProps={align ? { align } : undefined}
                >
                  {t(`releaseStageMembers.columns.${labelKey}`)}
                </SortableTableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        renderRow={(member, index) => (
          <TableRow
            key={member.TenantId}
            aria-rowindex={index + 1}
            className={hasFailed(member.JobStatus) ? classes.failedRow : undefined}
            data-testid={`release-stage-members-row-${member.TenantId}`}
          >
            <TableCell padding="checkbox">
              <Checkbox
                checked={selectedTenantIds.includes(member.TenantId)}
                onChange={() => toggleMember(member.TenantId)}
                inputProps={{ 'aria-label': member.Url ?? member.TenantName }}
              />
            </TableCell>
            {MEMBER_COLUMNS.map(({ columnId, value, align }, columnIndex) => {
              const cellValue = value(member);
              return (
                <TableCell
                  key={columnId}
                  align={align}
                  aria-colindex={columnIndex + 2}
                  aria-describedby={`release-stage-members-header-${columnId}`}
                  className={cellValue ? undefined : classes.emptyCell}
                >
                  {cellFor(columnId, cellValue, member)}
                </TableCell>
              );
            })}
          </TableRow>
        )}
      />
    );
  }

  return (
    <Dialog
      id={id}
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      aria-labelledby={`${id}-title`}
      data-testid="release-stage-members-dialog"
      slotProps={{ paper: { className: classes.dialogPaper } }}
    >
      <DialogTitle id={`${id}-title`} onClose={handleClose} closeLabel={t('global.close')}>
        {t('releaseStageMembers.title', { releaseStageName })}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.actions}>
          <ReleaseStageTargetVersionButton
            releaseStageId={releaseStageId}
            currentVersion={targetReleaseVersion}
            lastVersion={lastReleaseVersion}
            onUpdated={onReleaseStageUpdated}
          />
          <DefaultButton
            onClick={() => setAddSitesOpen(true)}
            data-testid="release-stage-members-add-sites"
          >
            {t('releaseStageMembers.actions.addSites')}
          </DefaultButton>
          <DefaultButton
            disabled={refreshing || selectedMembers.length === 0}
            onClick={openRemoveSites}
            data-testid="release-stage-members-remove-sites"
          >
            {t('releaseStageMembers.actions.removeSites')}
          </DefaultButton>
          <ReleaseStageUnlockActions
            releaseStageId={releaseStageId}
            members={running}
            selectedTenantIds={selectedRowTenantIds}
            onLockStatusesChange={updateLockStatuses}
          />
          <ReleaseStageUpgradeButton
            releaseStageId={releaseStageId}
            releaseStageName={releaseStageName}
            selectedInstallationIds={selectedInstallationIds}
            disabledReason={
              selectedUpgradeHasMissingInstallationId
                ? t('releaseStageMembers.upgradeMissingInstallationId')
                : undefined
            }
            onSuccess={reload}
          />
          <DefaultButton
            className={classes.refreshButton}
            onClick={reload}
            disabled={refreshing}
            startIcon={<Refresh />}
            data-testid="release-stage-members-refresh"
          >
            {t('releaseStageMembers.actions.refresh')}
          </DefaultButton>
        </div>
        <div className={classes.refreshBar}>
          {refreshing && (
            <LinearProgress
              ariaLabel={t('global.loadingIndicator.loading').toString()}
              data-testid="release-stage-members-refreshing"
            />
          )}
        </div>
        <div className={classes.versionSummary}>
          <Typography variant="body1" data-testid="release-stage-members-target-version">
            {t('releaseStageMembers.targetReleaseVersion')}{' '}
            <span className={classes.versionValue}>{targetReleaseVersion || EMPTY_VALUE}</span>
          </Typography>
          <Typography variant="body1" data-testid="release-stage-members-last-version">
            {t('releaseStageMembers.lastReleaseVersion')}{' '}
            <span className={classes.versionValue}>{lastReleaseVersion || EMPTY_VALUE}</span>
          </Typography>
          {jobSummary && (
            <Typography variant="body1" data-testid="release-stage-members-job-summary">
              {t('releaseStageMembers.jobStatusSummary')}{' '}
              <span className={classes.versionValue}>{jobSummary}</span>
            </Typography>
          )}
          <Typography
            variant="body2"
            className={classes.hiddenMembers}
            data-testid="release-stage-members-decommissioned"
          >
            {hiddenCount > 0 &&
              t('releaseStageMembers.hiddenMembers', {
                shown: rows.length,
                total: members.length,
                hidden: hiddenCount,
              })}
          </Typography>
        </div>
        <div className={classes.tableArea}>{content}</div>
        <AddReleaseStageSitesDialog
          id={`release-stage-members-add-sites-${releaseStageId}`}
          open={addSitesOpen}
          onClose={() => setAddSitesOpen(false)}
          onSitesAdded={handleMembershipChanged}
          releaseStageId={releaseStageId}
          releaseStageName={releaseStageName}
        />
        <RemoveReleaseStageSitesDialog
          id={`release-stage-members-remove-sites-${releaseStageId}`}
          open={removeSitesOpen}
          onClose={closeRemoveSites}
          onSitesRemoved={handleSitesRemoved}
          releaseStageId={releaseStageId}
          releaseStageName={releaseStageName}
          sites={removeSiteTargets}
        />
      </DialogContent>
    </Dialog>
  );
};
